import { Input as MuiInput, type InputProps as MuiInputProps } from '@mui/material';
import {
  forwardRef,
  useMemo,
  type CSSProperties,
  type ForwardRefExoticComponent,
  type PropsWithoutRef,
  type Ref,
  type RefAttributes,
} from 'react';
import * as stylex from '@stylexjs/stylex';
import {
  InputPrimitive,
  TextareaPrimitive,
  type InputPrimitiveProps,
  type TextareaPrimitiveProps,
} from '@figma/fpl-components';
import { color } from '@figma/fpl-tokens/tokens.stylex';
import { typographyStyles } from '@/UI/AppTypography/AppTypography';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { mergeRefs } from '@/utils/refs';
import { extractSxWidth, toStringValue } from './utils';

/**
 * `LineInput` is the borderless "line" input used for inline editing (file/tile rename,
 * design-app input headers, dense number fields). Historically it was the raw MUI `Input`
 * re-exported from `Input.tsx`.
 *
 * Migration (ADR-002 / ADR-004):
 * - Flag OFF (`fpl-migration`): renders the exact legacy MUI `Input` so the flag-off path stays
 *   byte-for-byte identical for rollback safety.
 * - Flag ON: renders an FPL primitive — `InputPrimitive` for single-line, `TextareaPrimitive`
 *   for multiline — styled by us into the line look. FPL has no styled "line" component
 *   (the styled `Input`/`Textarea` ship full chrome), so the unstyled primitives are the
 *   correct target, matching the "InputPrimitive which we can style ourselves" pattern.
 *
 * MUI-only APIs (`disableUnderline`, `startAdornment`/`endAdornment`, `error`, `variant`) are
 * intentionally NOT carried into the FPL path (ADR-001): the FPL branch owns its own chrome via
 * StyleX. `inputProps` remains a legacy API, but its native input semantics are mapped explicitly
 * so the FPL branch preserves product input behavior without spreading arbitrary MUI props.
 */
type LineInputFplTypography = 'textBodyMedium' | 'textBodySmallStrong';

type LineInputProps = MuiInputProps & {
  /**
   * FPL-only type token. Default `textBodyMedium` (12px). Use `textBodySmallStrong` (11px) when the
   * field must match an adjacent `label-sm` title — MUI `sx.typography` is not applied on the FPL path.
   */
  fplTypography?: LineInputFplTypography;
};

function fplTypographyStyle(token: LineInputFplTypography) {
  switch (token) {
    case 'textBodyMedium':
      return typographyStyles.textBodyMedium;
    case 'textBodySmallStrong':
      return typographyStyles.textBodySmallStrong;
    default: {
      const exhaustive: never = token;
      return exhaustive;
    }
  }
}

type LineInputElement = HTMLInputElement | HTMLTextAreaElement;

// FPL primitives are unstyled but type `className`/`style` out of their public props. FPL's own
// styled `Input` adds them back the same way; we mirror that so we can apply the line styling.
type Styleable = { className?: string; style?: CSSProperties };
const StyledInputPrimitive = InputPrimitive as unknown as ForwardRefExoticComponent<
  PropsWithoutRef<InputPrimitiveProps & Styleable> & RefAttributes<HTMLInputElement>
>;
const StyledTextareaPrimitive = TextareaPrimitive as unknown as ForwardRefExoticComponent<
  PropsWithoutRef<TextareaPrimitiveProps & Styleable> & RefAttributes<HTMLTextAreaElement>
>;

function isFplInputType(type: unknown): type is NonNullable<InputPrimitiveProps['type']> {
  return (
    type === 'number' ||
    type === 'search' ||
    type === 'text' ||
    type === 'tel' ||
    type === 'url' ||
    type === 'email' ||
    type === 'password'
  );
}

function isNativeInputConstraint(value: unknown): value is string | number {
  return typeof value === 'string' || typeof value === 'number';
}

const styles = stylex.create({
  field: {
    appearance: 'none',
    boxSizing: 'border-box',
    minWidth: 0,
    margin: 0,
    padding: 0,
    paddingBottom: '1px',
    backgroundColor: 'transparent',
    color: color.text,
    // The "line": a single bottom rule that strengthens on focus.
    borderTopWidth: 0,
    borderRightWidth: 0,
    borderLeftWidth: 0,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: { default: color.border, ':focus': color.borderSelected },
    outlineStyle: 'none',
    '::placeholder': { color: color.textSecondary, opacity: 1 },
  },
  fullWidth: {
    width: '100%',
  },
  multiline: {
    boxSizing: 'content-box',
    overflowY: 'hidden',
    resize: 'none',
  },
  disabled: {
    color: color.textDisabled,
    borderBottomColor: color.borderDisabled,
  },
});

/** FPL-backed implementation. Rendered only when the migration flag is on. */
const FplLineInput = forwardRef<LineInputElement, LineInputProps>(function FplLineInput(
  {
    multiline,
    value,
    defaultValue,
    onChange,
    onFocus,
    onBlur,
    onKeyDown,
    onKeyUp,
    onClick,
    onMouseDown,
    onMouseUp,
    placeholder,
    disabled,
    readOnly,
    autoFocus,
    fullWidth,
    name,
    id,
    required,
    inputRef,
    inputProps,
    rows,
    minRows,
    maxRows,
    sx,
    fplTypography = 'textBodyMedium',
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
  },
  ref,
) {
  // Both `ref` (component ref) and the legacy `inputRef` should point at the editable element,
  // which for an FPL primitive is the primitive's own ref.
  const setRef = useMemo(
    () => mergeRefs<LineInputElement>(ref, inputRef as Ref<LineInputElement> | undefined),
    [ref, inputRef],
  );

  const skin = stylex.props(
    fplTypographyStyle(fplTypography),
    styles.field,
    fullWidth && styles.fullWidth,
    Boolean(multiline) && styles.multiline,
    Boolean(disabled) && styles.disabled,
  );

  // `sx` is a legacy MUI API and is otherwise not carried into the FPL path (ADR-001), but we
  // honor an explicit `sx.width` so callers can size the field — the one sizing knob LineInput
  // needs. It overrides the default full-width style since inline style beats the class.
  const sxWidth = extractSxWidth(sx);
  const fieldStyle = sxWidth != null ? { ...skin.style, width: sxWidth } : skin.style;

  // FPL `onChange` is value-first; legacy callers expect the DOM event (`e.target.value`).
  // Re-emit the original event so call sites do not have to change.
  const handleChange = (_value: string, opts: { event: React.ChangeEvent<LineInputElement> }) => {
    onChange?.(opts.event);
  };

  const requestedInputType = inputProps?.type;
  const fplInputType = isFplInputType(requestedInputType) ? requestedInputType : undefined;
  const min = isNativeInputConstraint(inputProps?.min) ? inputProps.min : undefined;
  const max = isNativeInputConstraint(inputProps?.max) ? inputProps.max : undefined;
  const step = isNativeInputConstraint(inputProps?.step) ? inputProps.step : undefined;
  const nestedAriaLabel = typeof inputProps?.['aria-label'] === 'string' ? inputProps['aria-label'] : undefined;

  // FPL primitives require an accessible name (label or id). Pass through what the caller gave,
  // including the legacy nested aria-label, and fall back to a generic label so inline editors
  // are never unlabeled (see migration plan).
  const requestedAriaLabel = ariaLabel ?? nestedAriaLabel;
  const ariaLabelFallback =
    id != null || ariaLabelledby != null ? requestedAriaLabel : (requestedAriaLabel ?? 'Text input');

  const interactionHandlers = disabled
    ? undefined
    : {
        onChange: handleChange,
        onFocus,
        onBlur,
        onKeyDown,
        onKeyUp,
        onClick,
        onMouseDown,
        onMouseUp,
      };

  // Shared legacy MUI -> FPL primitive mapping. Legacy mouse/focus handlers are typed for the MUI
  // root element and `onChange` is event-first, but at runtime they receive the real field event,
  // so this is the intentional adapter-level compatibility cast described in ADR-002 (one cast
  // here rather than casting every prop). MUI-only props (sx, adornments, error, variant,
  // disableUnderline) are deliberately not forwarded (ADR-001).
  const common = {
    id,
    'aria-label': ariaLabelFallback,
    'aria-labelledby': ariaLabelledby,
    className: skin.className,
    style: fieldStyle,
    name,
    value: toStringValue(value),
    defaultValue: toStringValue(defaultValue),
    placeholder,
    disabled,
    readOnly,
    autoFocus,
    required,
    ...interactionHandlers,
  };

  if (multiline) {
    // Match MUI's multiline default: start at one line and auto-grow unless `rows`
    // explicitly asks for a fixed-height textarea.
    const numericRows = typeof rows === 'number' ? rows : typeof minRows === 'number' ? minRows : 1;
    return (
      <StyledTextareaPrimitive
        {...(common as unknown as TextareaPrimitiveProps & Styleable)}
        ref={setRef}
        rows={numericRows}
        expandable={typeof rows !== 'number'}
        maxHeight={typeof maxRows === 'number' ? `calc(${maxRows} * 1lh)` : undefined}
      />
    );
  }

  return (
    <StyledInputPrimitive
      {...(common as unknown as InputPrimitiveProps & Styleable)}
      ref={setRef}
      type={fplInputType}
      min={min}
      max={max}
      step={step}
    />
  );
});

FplLineInput.displayName = 'FplLineInput';

export const LineInput = forwardRef<LineInputElement, LineInputProps>(function LineInput(
  { fplTypography, ...props },
  ref,
) {
  const isFplMigrationEnabled = useFplMigrationFlag();

  // Flag off -> exact legacy component (the MUI `Input` previously re-exported as `LineInput`).
  if (!isFplMigrationEnabled) {
    return <MuiInput ref={ref} {...props} />;
  }

  return <FplLineInput ref={ref} fplTypography={fplTypography} {...props} />;
});

LineInput.displayName = 'LineInput';
